// /host/events — host's view of events across their venues.
//
// Tabbed by lifecycle state. The "New event" CTA opens the create form. List
// items deep-link into the manage view.

import {
  Anchor,
  Box,
  Button,
  Card,
  Group,
  Loader,
  SegmentedControl,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { createFileRoute,Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { AppShell } from '../../../components/app-shell/AppShell';
import { StateBadge } from '../../../components/StateBadge';
import type { AnyState } from '../../../lib/display-state';

export const Route = createFileRoute('/host/events/')({
  component: HostEventsPage,
});

type EventListItem = {
  id: string;
  title: string;
  startsAt: number;
  endsAt: number;
  slotsTotal: number;
  slotsFilled: number;
  slotsRemaining: number;
  state: AnyState['event'];
  createdAt: number;
  venue: { id: string; slug: string; name: string; city: string; country: string };
};

type VenueRow = {
  id: string;
  name: string;
  city: string;
  status: 'draft' | 'pending_review' | 'live' | 'paused' | 'removed';
};

type Tab = 'published' | 'draft' | 'closed' | 'cancelled';

function HostEventsPage() {
  const [byState, setByState] = useState<Record<Tab, EventListItem[] | null>>({
    published: null,
    draft: null,
    closed: null,
    cancelled: null,
  });
  const [venues, setVenues] = useState<VenueRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('published');

  // Load the host's venues so we can tag drafts/cancelled events to a venue
  // (the server requires venue_id on those filter calls).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch('/api/venues/me/venues', { credentials: 'include' });
        const json = (await res.json()) as {
          ok: boolean;
          venues?: VenueRow[];
          error?: { message?: string };
        };
        if (cancelled) return;
        if (!res.ok || !json.ok) {
          setError(json.error?.message ?? "Couldn't load your venues.");
          return;
        }
        setVenues(json.venues ?? []);
      } catch {
        if (!cancelled) setError('Network problem loading your venues.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Once venues are loaded, fetch each tab. Published + closed are public, so
  // we can ask without a venue filter; draft + cancelled require venue_id, so
  // we paginate per venue. With small venue counts this is fine.
  useEffect(() => {
    if (venues === null) return;
    let cancelled = false;
    void (async () => {
      try {
        const venueIds = new Set(venues.map((v) => v.id));
        const fetchState = async (state: Tab): Promise<EventListItem[]> => {
          if (state === 'draft' || state === 'cancelled') {
            const all: EventListItem[] = [];
            for (const v of venues) {
              const params = new URLSearchParams({
                state,
                venue_id: v.id,
                limit: '50',
              });
              const res = await fetch(`/api/events?${params.toString()}`, {
                credentials: 'include',
              });
              const json = (await res.json()) as {
                ok: boolean;
                items?: EventListItem[];
              };
              if (res.ok && json.ok) all.push(...(json.items ?? []));
            }
            return all;
          }
          const params = new URLSearchParams({ state, limit: '100' });
          const res = await fetch(`/api/events?${params.toString()}`, {
            credentials: 'include',
          });
          const json = (await res.json()) as { ok: boolean; items?: EventListItem[] };
          if (!res.ok || !json.ok) return [];
          return (json.items ?? []).filter((it) => venueIds.has(it.venue.id));
        };

        const [published, draft, closed, cancelledItems] = await Promise.all([
          fetchState('published'),
          fetchState('draft'),
          fetchState('closed'),
          fetchState('cancelled'),
        ]);
        if (cancelled) return;
        setByState({
          published,
          draft,
          closed,
          cancelled: cancelledItems,
        });
      } catch {
        if (!cancelled) setError('Network problem loading events.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [venues]);

  const counts = useMemo(
    () => ({
      published: byState.published?.length ?? 0,
      draft: byState.draft?.length ?? 0,
      closed: byState.closed?.length ?? 0,
      cancelled: byState.cancelled?.length ?? 0,
    }),
    [byState],
  );

  const visible = byState[tab];

  return (
    <AppShell tone="content">
        <Stack gap="lg">
          <Group justify="space-between" align="flex-end" wrap="wrap">
            <Stack gap={4}>
              <Title order={1}>Your events</Title>
              <Text c="dimmed">
                The gigs you're posting and the artists who've raised their hand.
              </Text>
            </Stack>
            <Button
              component={Link}
              to="/host/events/new"
              variant="gradient"
              gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
            >
              Post a new event
            </Button>
          </Group>

          {error !== null ? <Text c="red">{error}</Text> : null}

          <SegmentedControl
            value={tab}
            onChange={(v) => {
              if (v === 'published' || v === 'draft' || v === 'closed' || v === 'cancelled') {
                setTab(v);
              }
            }}
            data={[
              { value: 'published', label: `Open (${counts.published})` },
              { value: 'draft', label: `Drafts (${counts.draft})` },
              { value: 'closed', label: `Closed (${counts.closed})` },
              { value: 'cancelled', label: `Cancelled (${counts.cancelled})` },
            ]}
            fullWidth
          />

          {visible === null ? (
            <Group justify="center" py="xl">
              <Loader />
            </Group>
          ) : visible.length === 0 ? (
            <EmptyState tab={tab} />
          ) : (
            <Stack gap="sm">
              {visible.map((event) => (
                <EventRow key={event.id} event={event} />
              ))}
            </Stack>
          )}
        </Stack>
    </AppShell>
  );
}

function EventRow({ event }: { event: EventListItem }) {
  return (
    <Link
      to="/host/events/$id/manage"
      params={{ id: event.id }}
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <Card withBorder padding="md" radius="md">
        <Group justify="space-between" wrap="nowrap">
          <Stack gap={2}>
            <Text fw={600}>{event.title}</Text>
            <Text size="sm" c="dimmed">
              {event.venue.name} · {formatRange(event.startsAt, event.endsAt)}
            </Text>
          </Stack>
          <Group gap="xs" wrap="nowrap">
            <Text size="sm" c="dimmed">
              {event.slotsFilled}/{event.slotsTotal} filled
            </Text>
            <StateBadge entity="event" state={event.state} />
          </Group>
        </Group>
      </Card>
    </Link>
  );
}

function EmptyState({ tab }: { tab: Tab }) {
  const message =
    tab === 'draft'
      ? 'No drafts. Start one and save when you\'re ready to share.'
      : tab === 'closed'
        ? 'No closed events yet.'
        : tab === 'cancelled'
          ? 'No cancellations.'
          : 'No open events. Post one to invite artists in.';
  return (
    <Card withBorder padding="xl" radius="md">
      <Stack align="center" gap="sm">
        <Text c="dimmed">{message}</Text>
        <Anchor component={Link} to="/host/events/new">
          Post a new event
        </Anchor>
      </Stack>
    </Card>
  );
}

function formatRange(startMs: number, endMs: number): string {
  const start = new Date(startMs);
  const end = new Date(endMs);
  const dateFmt = new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();
  if (sameDay) return dateFmt.format(start);
  return `${dateFmt.format(start)} → ${dateFmt.format(end)}`;
}
